import React from "react";

// CrewZNatioN official event wordmark — two distinct owner-approved
// treatments, both routed through this single component:
//
//   INLINE (variant="inline", default) — used when both words appear
//   on the same line: renders `THE GATHERING`, ALL CAPITALS, with
//   `THE` in institutional white and `GATHERING` in institutional
//   gold. Non-breaking (protected LTR lockup); safe inside RTL body
//   copy. See the design brief for the "one-line" case.
//
//   STACKED (variant="stacked") — used when the two words appear on
//   separate lines: renders `The` (title case, white) on line 1 and
//   `Gathering` (title case, gold) on line 2. The two lines are
//   horizontally centred so the initial `T` in `The` sits vertically
//   over the lowercase `h` in `Gathering` (matching the owner's
//   reference lockup). Compact but clearly separated vertical
//   spacing preserved via a tuned `line-height` that responds to
//   viewport.
//
// The two treatments are mutually exclusive: choose based on layout,
// not on translation. When the wordmark appears inline inside prose
// (Arabic or English), always use the inline treatment. When it
// appears as a two-line lockup inside a display heading, use the
// stacked treatment.
//
// Canonical case (inline `THE GATHERING`, stacked `The Gathering`)
// is protected against parent `text-transform` rules via explicit
// `textTransform: "none"` on each token so ambient
// `uppercase`/`lowercase` rules cannot alter the case.
//
// Colours never inherit and never use alternate gold shades or
// opacity variants. Two surface variants are supported so the
// wordmark remains legible on every background:
//
//   surface="dark" (default) — dark surfaces:
//     • Line 1 / THE       → var(--crewz-text)       (white)
//     • Line 2 / GATHERING → var(--crewz-gold)       (gold)
//
//   surface="light" — light / ivory surfaces:
//     • Line 1 / THE       → var(--crewz-text-dark)  (dark neutral)
//     • Line 2 / GATHERING → var(--crewz-gold)       (gold)
//
// Font family, size, and weight are inherited from the surrounding
// heading/body so the wordmark integrates cleanly into the host
// typography.
//
// Reuse this component wherever the branded event name appears in
// user-facing content. See PRD site-wide brand standard (Feb 2026).
export default function EventName({ surface = "dark", variant = "inline" }) {
    const topColor =
        surface === "light"
            ? "text-[var(--crewz-text-dark)]"
            : "text-[var(--crewz-text)]";

    if (variant === "stacked") {
        return (
            <span
                dir="ltr"
                className="inline-flex flex-col items-center leading-[0.92] md:leading-[0.82]"
                style={{ textTransform: "none" }}
            >
                <span className={topColor} style={{ textTransform: "none" }}>The</span>
                <span className="text-[var(--crewz-gold)]" style={{ textTransform: "none" }}>Gathering</span>
            </span>
        );
    }

    return (
        <span
            dir="ltr"
            className="inline"
            style={{ textTransform: "none", whiteSpace: "nowrap" }}
        >
            <span className={topColor} style={{ textTransform: "none" }}>THE</span>
            {"\u00A0"}
            <span className="text-[var(--crewz-gold)]" style={{ textTransform: "none" }}>GATHERING</span>
        </span>
    );
}
